import React, {
  ForwardedRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';

import ChevronDownLight from 'components/Common/Icons/ChevronDownLight';
import ChevronUpLight from 'components/Common/Icons/ChevronUpLight';

import styles from '../ProductDescription.module.scss';

type Props = { children: React.ReactElement };
export type Ref = { toggleCollapse: () => void };

function CollapseText(
  { children }: Props,
  ref: ForwardedRef<Ref>
): JSX.Element {
  const [isCollapse, setCollapse] = useState<boolean>(false);
  const [isReady, setReady] = useState<boolean>(false);
  const [canBeCollapse, setCanBeCollapse] = useState<boolean>(false);
  const refChildren = useRef<HTMLElement>(null);
  const calculateCollapse = useCallback(() => {
    const el = refChildren.current as HTMLElement;

    const elementCloned = el.cloneNode(true) as HTMLElement;
    elementCloned.style.height = '0';
    elementCloned.style.display = 'inline-block';
    elementCloned.style.whiteSpace = 'nowrap';
    elementCloned.style.textOverflow = 'clip';
    elementCloned.classList.remove(styles.collasible);

    el.after(elementCloned);

    setCanBeCollapse(
      elementCloned.scrollWidth + 20 > el.getBoundingClientRect().width
    );

    elementCloned.remove();
  }, []);

  function refFunctions() {
    return {
      toggleCollapse: () => {
        setCollapse(isCollapse => !isCollapse);
      },
    };
  }

  useImperativeHandle(ref, refFunctions, []);

  useEffect(() => {
    refChildren.current && calculateCollapse();
    refChildren.current && window.addEventListener('resize', calculateCollapse);

    return () => {
      window.removeEventListener('resize', calculateCollapse);
    };
  }, [refChildren]);

  useEffect(() => {
    setReady(true);
  }, []);

  return (
    <>
      {canBeCollapse && <CollapseIcon isCollapse={isCollapse} />}
      {React.cloneElement(children, {
        ref: refChildren,
        className: classNames(children.props.className, styles.collapse, {
          [styles.collasible]: (isReady && !canBeCollapse) || isCollapse,
        }),
      })}
    </>
  );
}

function CollapseIcon({ isCollapse }: { isCollapse: boolean }): JSX.Element {
  const Component = isCollapse ? ChevronUpLight : ChevronDownLight;

  return <Component size={10} className={styles.arrow} />;
}

export default React.forwardRef(CollapseText);
